import { plural } from '../lib/format';
import { useResource } from '../lib/hooks';
import type { Capacity } from '../lib/types';
import { Callout, Lamp } from './ui';

type Which = 'tasks' | 'sandboxes';

/** GET /v1/capacity: slots on online Macs that take each session. Fleet-wide, so it changes slowly. */
export function useCapacity() {
  return useResource<Capacity>('/v1/capacity', { interval: 15000 });
}

/**
 * The toolbar line, only when it changes what happens to new work: no Mac
 * online means it waits. Slot counts are the fleet's business, not the
 * customer's, so a healthy fleet shows nothing here.
 */
export function CapacityMeta({ cap }: { cap: Capacity | undefined }) {
  if (!cap || cap.boxesOnline > 0) return null;
  return (
    <span className="live" title="No Mac in the fleet has reported in. New work queues until one does.">
      <Lamp tone="refused" />
      No Macs online: new work waits
    </span>
  );
}

/**
 * Macs are online but none has a headless slot free: the plane refuses
 * that work at once (409 no-capacity), so say so before anyone tries.
 * GUI alone missing (no slot has a logged-in desktop) says nothing here:
 * the console and the agent prompt start headless work, and a GUI request
 * gets the 409 and its fix in the answer.
 */
export function CapacityWarning({ cap, which }: { cap: Capacity | undefined; which: Which }) {
  if (!cap || cap.boxesOnline === 0) return null;
  const slots = cap[which];
  if (slots.headless > 0) return null;
  const noun = which === 'tasks' ? 'tasks' : 'sandboxes';
  return (
    <div className="band band--tight">
      <Callout tone="warn" title={`No Mac is free for ${noun} right now`}>
        {plural(cap.boxesOnline, 'Mac')} {cap.boxesOnline === 1 ? 'is' : 'are'} online, but none has a slot free for {noun}, so new ones are refused with <code>409 no-capacity</code>. Try again shortly.
      </Callout>
    </div>
  );
}
